import { Icon } from './icons'

/**
 * Quem escreveu, em 22 pixels.
 *
 * A foto quando existe, as iniciais sobre a cor da pessoa quando não. Sempre um círculo do mesmo
 * tamanho, porque uma fileira de bolhas onde só alguns têm foto vira uma coluna desalinhada — e
 * o alinhamento é o que permite varrer a conversa e ver de quem é cada fala sem ler nome nenhum.
 *
 * `title` sempre presente: a foto responde "quem" para quem já conhece a pessoa e não responde
 * nada para quem chegou ontem.
 */
export function AgentAvatar({
  name,
  color,
  avatarUrl,
  size = 22,
  className = '',
}: {
  name: string | null
  color?: string | null
  avatarUrl?: string | null
  size?: number
  className?: string
}) {
  const label = (name ?? '').trim()

  /**
   * Sem nome não se inventam iniciais.
   *
   * 1.356 das 1.506 mensagens que saíram daqui não têm autor: foram escritas no console do
   * marketplace ou no celular e importadas depois. Sabemos que são nossas e que uma pessoa as
   * escreveu — não quem. Um "??" nesse lugar lê como defeito; a marca neutra abaixo diz a
   * verdade, que é "equipe, escrita fora do SAC".
   */
  if (!label) {
    return (
      <span
        className={`avatar avatar-anon ${className}`}
        title="Equipe — escrita fora do SAC, sem autor identificado"
        style={{ width: size, height: size }}
      >
        <Icon name="user" size={Math.round(size * 0.55)} />
      </span>
    )
  }

  const initials =
    label
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((p) => p[0])
      .join('')
      .toUpperCase()

  if (avatarUrl) {
    return (
      <img
        className={`avatar-img ${className}`}
        src={avatarUrl}
        alt={label}
        title={label}
        width={size}
        height={size}
        style={{ width: size, height: size }}
      />
    )
  }

  return (
    <span
      className={`avatar ${className}`}
      title={label}
      style={{
        background: color ?? '#7c5cf5',
        width: size,
        height: size,
        // A 22px as iniciais precisam encolher junto, senão elas transbordam o círculo.
        fontSize: Math.max(9, Math.round(size * 0.4)),
      }}
    >
      {initials}
    </span>
  )
}

/**
 * Reduz a imagem no navegador antes de subir.
 *
 * Um avatar é uma miniatura, e a foto que sai de um celular tem alguns megabytes. Sem isto a
 * coluna guardaria o original inteiro e ele viajaria em toda listagem de usuários. Recorta pelo
 * centro para um quadrado, porque o destino é um círculo — deixar o navegador espremer a foto
 * distorceria o rosto.
 */
export async function shrinkToAvatar(file: File, side = 150): Promise<string> {
  const bitmap = await createImageBitmap(file)
  const menor = Math.min(bitmap.width, bitmap.height)
  const canvas = document.createElement('canvas')
  canvas.width = side
  canvas.height = side
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('canvas indisponível')
  ctx.drawImage(
    bitmap,
    (bitmap.width - menor) / 2,
    (bitmap.height - menor) / 2,
    menor,
    menor,
    0,
    0,
    side,
    side,
  )
  bitmap.close()
  // JPEG e não PNG: uma foto em PNG a 150px ainda passa de 40 KB, e o teto do servidor é 400 KB.
  return canvas.toDataURL('image/jpeg', 0.85)
}
